import { useEffect, useState } from 'react';
import { api } from './api.js';
import { stamp } from './format.js';

/**
 * The site plan tab.
 *
 * Deliberately not a drawing tool. Planning annotates in whatever they already use and
 * uploads the result, so what is on screen is a SNAPSHOT: this is what the ground looked
 * like as understood at this time, put there by this person. An editable canvas has no good
 * answer to "whose version is current" halfway through an incident; a dated image does.
 *
 * The current version fills the screen because that is the job — this gets put on a wall
 * display. Earlier versions are a strip underneath, so the story of how the picture changed
 * is one click away rather than lost behind the newest upload.
 */

const ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX_BYTES = 12 * 1024 * 1024;

const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

export function SitePlan({ incidentId, session, incident }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState('');
  const [viewing, setViewing] = useState(null);  // a version opened full screen
  const [note, setNote] = useState(null);

  const mayUpload = session.permissions.includes('siteplan.write')
    && incident?.status !== 'closed';

  async function load() {
    try { setData(await api.sitePlans(incidentId)); }
    catch { setError('Could not load the site plan.'); }
  }
  useEffect(() => { load(); }, [incidentId]);

  async function upload(file) {
    if (!file) return;
    setError(null); setNote(null);

    // Checked here as well as on the server. Not as security — the server does not trust
    // this — but so somebody who picks a 40MB photo is told immediately rather than after
    // waiting for the whole thing to upload and be refused.
    if (file.size > MAX_BYTES) {
      setError(`That file is ${kb(file.size)}. The limit is 12 MB — export it a little smaller.`);
      return;
    }
    setBusy(true);
    try {
      const out = await api.uploadSitePlan(incidentId, file, caption.trim());
      setCaption('');
      if (out.duplicateOf) {
        setNote(`That is the same file as version ${out.duplicateOf.version}, so nothing was added.`);
      }
      await load();
    } catch (err) {
      setError(err.detail || 'That upload did not save.');
    } finally { setBusy(false); }
  }

  if (!data) {
    return <div className="card"><div className="empty">{error ?? 'Loading…'}</div></div>;
  }

  const { current, plans } = data;
  const earlier = plans.filter((p) => p.id !== current?.id);

  return (
    <div className="card sitePlan">
      <h2>
        Site plan
        {current && <span className="pill">v{current.version}</span>}
      </h2>

      {error && <p className="err inline">{error}</p>}
      {note && <p className="okNote">{note}</p>}

      <div className="body">
        {mayUpload && (
          <div className="spUpload">
            <input type="text" className="spCaption" value={caption} maxLength={300}
                   onChange={(e) => setCaption(e.target.value)}
                   placeholder="What changed — e.g. cordon extended east after the wind shift" />
            <label className={`btn narrow ${busy ? 'off' : ''}`}>
              {busy ? 'Uploading…' : current ? 'Upload a new version' : 'Upload the site plan'}
              <input type="file" accept={ACCEPT} disabled={busy} hidden
                     onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
            <small>JPEG, PNG or WebP, up to 12 MB. Annotate it in whatever you normally use.</small>
          </div>
        )}

        {!current ? (
          <div className="empty">
            No site plan yet.
            {mayUpload
              ? ' Mark up your plot plan however you normally would and upload the picture.'
              : ' Planning or the incident commander can upload one.'}
          </div>
        ) : (
          <>
            <figure className="spCurrent">
              <button className="spImgBtn" onClick={() => setViewing(current)}
                      title="Open full screen">
                <img src={api.sitePlanUrl(incidentId, current.id)}
                     width={current.width || undefined} height={current.height || undefined}
                     alt={current.caption || `Site plan version ${current.version}`} />
              </button>
              <figcaption>
                <b>v{current.version}</b>
                {current.caption && <span className="cap">{current.caption}</span>}
                <span className="spacer" />
                <span className="who">
                  {current.uploaded_by_name || 'Unknown'} · {stamp(current.uploaded_at)}
                </span>
                {mayUpload && (
                  <button className="hoBtn small" disabled={busy}
                          title="Take it off the board. The version stays in the record."
                          onClick={async () => {
                            setBusy(true);
                            try { await api.withdrawSitePlan(incidentId, current.id); await load(); }
                            catch { setError('That did not save.'); }
                            finally { setBusy(false); }
                          }}>
                    Withdraw
                  </button>
                )}
              </figcaption>
            </figure>

            {earlier.length > 0 && (
              <>
                <h3 className="sectionHead">Earlier versions</h3>
                <ul className="spHistory">
                  {earlier.map((p) => (
                    <li key={p.id} className={p.withdrawn_at ? 'withdrawn' : ''}>
                      <button className="spThumbBtn" onClick={() => setViewing(p)}>
                        <img src={api.sitePlanUrl(incidentId, p.id)} alt="" loading="lazy" />
                      </button>
                      <div className="spMeta">
                        <b>v{p.version}</b>
                        {p.withdrawn_at && <span className="pill retired">withdrawn</span>}
                        {p.caption && <span className="cap">{p.caption}</span>}
                        <span className="who">
                          {p.uploaded_by_name || 'Unknown'} · {stamp(p.uploaded_at)} · {kb(p.bytes)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>

      {viewing && (
        <SitePlanViewer incidentId={incidentId} plan={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

/**
 * Full screen, because a plot plan on a laptop is unreadable otherwise.
 *
 * Click to toggle between fit-to-screen and actual size, and drag to move around when
 * zoomed. No zoom controls: on a wall display nobody is going to find a slider, and pinch
 * works on a tablet without anything from us.
 */
function SitePlanViewer({ incidentId, plan, onClose }) {
  const [zoomed, setZoomed] = useState(false);

  // Escape closes it. On a wall display the mouse may be nowhere near the corner.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="spViewer" onClick={onClose} role="dialog" aria-modal="true"
         aria-label={`Site plan version ${plan.version}`}>
      <div className="spViewerBar" onClick={(e) => e.stopPropagation()}>
        <b>v{plan.version}</b>
        {plan.caption && <span className="cap">{plan.caption}</span>}
        <span className="spacer" />
        <span className="who">{plan.uploaded_by_name || 'Unknown'} · {stamp(plan.uploaded_at)}</span>
        <a className="hoBtn small" href={api.sitePlanUrl(incidentId, plan.id)}
           target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
          Open in a new tab
        </a>
        <button className="hoX" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className={`spStage ${zoomed ? 'zoom' : ''}`} onClick={(e) => e.stopPropagation()}>
        <img src={api.sitePlanUrl(incidentId, plan.id)}
             alt={plan.caption || `Site plan version ${plan.version}`}
             onClick={() => setZoomed((z) => !z)}
             title={zoomed ? 'Click to fit to the screen' : 'Click to see it at full size'} />
      </div>
    </div>
  );
}
